import { MimeType, RNWebView } from '../Types'
import { DOWNLOAD_FILE_REACT_NATIVE_MESSAGE_TYPE } from './consts'
import { stringifyError } from './Errors'

const userAgent = navigator.userAgent
const regex = /Portal-([\d.]+)/
const match = regex.exec(userAgent)
const version = match ? match[1] : null

export const isOpenedInWebview = (): boolean => {
  /* In react native the applicationNameForUserAgent property is used to include the value Portal */
  return userAgent.includes('Portal')
}

export const getWebviewPortalVersion = (): string | null => {
  return version
}

export enum VersionComparison {
  Greater = 1,
  Equal = 0,
  Lesser = -1,
}

/**
 * Compare two portal versions with each other and return the result
 * @param v1 portal version like 1.0.0
 * @param v2 portal version like 1.0.0
 * @returns 1 if v1 is greater, 0 if equal, -1 if lesser
 */
export const comparePortalVersions = (v1: string, v2: string): VersionComparison => {
  const parts1 = v1.split('.').map(Number)
  const parts2 = v2.split('.').map(Number)

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const num1 = parts1[i] ?? 0 // Default to 0 if undefined
    const num2 = parts2[i] ?? 0

    if (num1 > num2) return VersionComparison.Greater
    if (num1 < num2) return VersionComparison.Lesser
  }
  return VersionComparison.Equal
}

/**
 * Handle file download on react native side
 */
export const handleReactNativeFileDownload = (arrayBuffer: ArrayBuffer, fileName: string, fileExtension: string, fileMimeType: MimeType): void => {
  const WebViewWindow = window as unknown as RNWebView

  /* Convert ArrayBuffer to base64 string */
  let base64String: string
  try {
    base64String = Buffer.from(arrayBuffer).toString('base64')
  } catch (error) {
    const errMessage = {
      data: stringifyError(error as Error),
      message: 'Error converting arrayBuffer to base64',
      type: 'error',
    }
    WebViewWindow?.ReactNativeWebView?.postMessage(JSON.stringify(errMessage))
    console.error('Error converting arrayBuffer to base64:', error)
    return
  }

  const fileData = {
    url: base64String,
    name: fileName,
    extension: fileExtension,
    mime: fileMimeType,
    type: DOWNLOAD_FILE_REACT_NATIVE_MESSAGE_TYPE,
  }

  WebViewWindow?.ReactNativeWebView?.postMessage(JSON.stringify(fileData))
}

/**
 * When we have urls for direkt file downloads like from AWS, react native should handle that by itself
 */
export const handleReactNativeFileDownloadFromUrl = (url: string, fileName: string, fileExtension: string, fileMimeType: MimeType): void => {
  const WebViewWindow = window as unknown as RNWebView

  const fileData = {
    url,
    name: fileName,
    extension: fileExtension,
    mime: fileMimeType,
    type: DOWNLOAD_FILE_REACT_NATIVE_MESSAGE_TYPE,
    useUrl: true,
  }

  WebViewWindow?.ReactNativeWebView?.postMessage(JSON.stringify(fileData))
}

//#region Flow R2 webview functions
//TODO remove everything below once Flow R3 is released and all the users have updated their apps

/** Return types of react-native's Platform.OS */
const REACT_NATIVE_PLATFORMS = ['ios', 'android', 'windows', 'macos', 'web'] as const
type REACT_NATIVE_PLATFORM = (typeof REACT_NATIVE_PLATFORMS)[number]

const REACT_NATIVE_PLATFORMS_SET = new Set(REACT_NATIVE_PLATFORMS)

/**
 * React-native in hager-energy-mobile-apps injects a global variable 'isInWebView' into the window object,
 * which has the value of react-native's Platform.OS.
 *
 * @returns false if the app is NOT in react-native's webview. The Platform.OS value if it is.
 */
export const isOpenedInWebviewFlowR2 = (): REACT_NATIVE_PLATFORM | false | true => {
  // is detected by android phones
  if ('ReactNativeWebView' in window) return true

  const ua = navigator.userAgent
  const isIOS = /iphone|ipad|ipod/i.test(ua)
  const isRealBrowser = /safari\//i.test(ua) || /crios\//i.test(ua) || /fxios\//i.test(ua)
  if (isIOS && !isRealBrowser) return true

  if (!('isInWebView' in window)) return false
  const isInWebView = window.isInWebView as REACT_NATIVE_PLATFORM | false
  if (typeof isInWebView !== 'string' || !REACT_NATIVE_PLATFORMS_SET.has(isInWebView)) return false

  return isInWebView
}
